import { AppAttribute } from "@interfaces/Internal/AppAttribute";

/**
 * Атрибут, из которого миграционный пайплайн реально строит имя папки с
 * картинками (py_scripts/sync/build_new_sku.py): model_title = product_title
 * старого сайта пишется в DCITY одновременно и как "Наименование", и (через
 * slugify(model_title)) как средний сегмент пути картинки
 * (<бренд>/<MODEL_SLUG>/<цвет>) — см. build_images()/compute_dest_rel_path().
 * "Артикул модели" — это НЕ название модели, а код ценовой группы старого
 * сайта (price_group_dict по display_id, тот же at_id 311) — к имени папки
 * отношения не имеет.
 */
const MODEL_NAME_ATTRIBUTE_NAME = "Наименование";

/**
 * JS-копия slugify() из build_new_sku.py: re.sub(r"\W", "_", value).upper().
 * Python \w в Unicode-режиме (по умолчанию для str) — это буквы/цифры/"_"
 * ЛЮБОГО алфавита, не только ASCII, поэтому кириллица здесь не ASCII-транслит,
 * а просто сохраняется как есть — заменяются только пробелы/пунктуация.
 * Без этого поиск по "сырому" названию с пробелами ("W Distance Wind Shell")
 * не найдёт папку "W_DISTANCE_WIND_SHELL" — ILIKE ищет точную подстроку.
 */
const toFolderSlug = (value: string) => value.replace(/[^\p{L}\p{N}_]/gu, "_").toUpperCase();

export interface FilePickerAnchor {
    anchorFileId: number | null;
    initialSearchTerm: string | undefined;
}

/**
 * Куда открыть FilePickerDialog, чтобы дерево сразу показывало нужную папку,
 * а не вершину. Используется и в "Добавить из хранилища" (групповое
 * добавление), и при выборе файла для конкретной строки (в том числе для
 * только что созданной пустой строки через "Новое значение") — решение
 * одинаковое в обоих случаях:
 * - если у атрибута уже есть другое фото — берём его как якорь
 *   (FilePickerDialog сам резолвит его папку и скроллит к родителю, т.е. к
 *   папке модели);
 * - если фото нет вовсе — ищем папку по названию модели (атрибут
 *   "Наименование" карточки).
 */
export const resolveFilePickerAnchor = (
    attrs: AppAttribute[] | null | undefined,
    existingFileIds: number[]
): FilePickerAnchor => {
    const anchorFileId = existingFileIds[0] ?? null;

    if (anchorFileId) {
        return { anchorFileId, initialSearchTerm: undefined };
    }

    const modelNameRaw = attrs?.find((el) => el.a_name === MODEL_NAME_ATTRIBUTE_NAME)?.items?.[0]
        ?.value as string | undefined;

    return {
        anchorFileId: null,
        initialSearchTerm: modelNameRaw ? toFolderSlug(modelNameRaw) : undefined,
    };
};
